import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import izenuAPI from '../../services/izenuAPI';

// Distributor settings: the distributor's own details, from
// api/distributor/settings/, which reads the distributor from the token, never
// from the request. (The ISRC generator is platform-wide: admin → Definições.)

const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500 disabled:bg-gray-100 disabled:text-gray-500';
const btnPrimary = 'rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium !text-white hover:bg-amber-600 disabled:opacity-50';

// The payout account lives in Financeiro → Pagamentos → Dados bancários (validated,
// titular + banco + IBAN or Multicaixa). distributor.iban is the old free-text field.
const PROFILE_FIELDS = ['name', 'email', 'nif', 'phone', 'address'];

function Field({ label, hint, error, wide, children }) {
    return (
        <label className={`block ${wide ? 'sm:col-span-2' : ''}`}>
            <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
            {children}
            {error
                ? <span className="mt-1 block text-xs text-red-600">{error}</span>
                : hint ? <span className="mt-1 block text-xs text-gray-500">{hint}</span> : null}
        </label>
    );
}

function Section({ title, subtitle, aside, children }) {
    return (
        <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                    {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
                </div>
                {aside}
            </div>
            {children}
        </section>
    );
}



const formatDate = (s) => s ? new Date(s.replace(' ', 'T')).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

export default function Settings() {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [profile, setProfile] = useState(null);
    const [form, setForm] = useState({});
    const [profileMsg, setProfileMsg] = useState(null); // { ok, text, field? }
    const [savingProfile, setSavingProfile] = useState(false);


    const load = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const { data } = await izenuAPI.get('/distributor/settings/fetch.php');
            if (!data.response) throw new Error(data.message);
            setProfile(data.distributor);
            setForm(Object.fromEntries(PROFILE_FIELDS.map(f => [f, data.distributor[f] ?? ''])));
        } catch (e) {
            console.log(e);
            setLoadError('Não foi possível carregar as definições.');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const profileChanges = profile
        ? Object.fromEntries(PROFILE_FIELDS.filter(f => String(form[f] ?? '') !== String(profile[f] ?? '')).map(f => [f, form[f]]))
        : {};
    const profileDirty = Object.keys(profileChanges).length > 0;

    const saveProfile = async (e) => {
        e.preventDefault();
        if (!profileDirty) return;
        if (profileChanges.email && !window.confirm('O email é o login do painel. A partir de agora entras com o novo email. Continuar?')) return;
        setSavingProfile(true);
        setProfileMsg(null);
        try {
            const { data } = await izenuAPI.post('/distributor/settings/update.php', profileChanges);
            if (data.response) {
                setProfile(data.distributor);
                setForm(Object.fromEntries(PROFILE_FIELDS.map(f => [f, data.distributor[f] ?? ''])));
                setProfileMsg({ ok: true, text: 'Dados guardados.' });
            } else {
                setProfileMsg({ ok: false, text: data.message || 'Não foi possível guardar.', field: data.field });
            }
        } catch (err) {
            console.log(err);
            setProfileMsg({ ok: false, text: 'Não foi possível guardar.' });
        }
        setSavingProfile(false);
    };


    if (loading) {
        return <div className="mx-auto max-w-4xl p-3 sm:p-6 text-left text-sm text-gray-500">A carregar…</div>;
    }
    if (loadError) {
        return (
            <div className="mx-auto max-w-4xl p-3 sm:p-6 text-left">
                <p className="text-sm text-red-600">{loadError}</p>
                <button type="button" onClick={load} className={`${btnPrimary} mt-3`}>Tentar novamente</button>
            </div>
        );
    }

    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
    const fieldError = (k) => (profileMsg && !profileMsg.ok && profileMsg.field === k) ? profileMsg.text : undefined;

    return (
        <div className="mx-auto max-w-4xl p-3 sm:p-6 text-left space-y-6">
            <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Definições</h1>
                <p className="text-sm text-gray-500">
                    {profile.name} · ID {profile.distributor_id}{profile.created_at ? ` · desde ${formatDate(profile.created_at).split(',')[0]}` : ''}
                </p>
            </div>

            {/* Profile */}
            <Section title="Dados da distribuidora" subtitle="Usados nos relatórios, nas facturas e no contacto com a Izenu.">
                <form onSubmit={saveProfile}>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Nome *" error={fieldError('name')}>
                            <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} />
                        </Field>
                        <Field label="Email *" hint="É o login do painel." error={fieldError('email')}>
                            <input className={inputCls} type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                        </Field>
                        <Field label="NIF">
                            <input className={inputCls} value={form.nif} onChange={e => set('nif', e.target.value)} />
                        </Field>
                        <Field label="Contacto">
                            <input className={inputCls} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} />
                        </Field>
                        <div className="sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                            Os dados bancários para os pagamentos da Izenu (vendas e royalties) estão em{' '}
                            <Link to="/payouts" className="font-semibold underline">Financeiro → Pagamentos → Dados bancários</Link>.
                        </div>
                        <Field label="Endereço" wide>
                            <input className={inputCls} value={form.address} onChange={e => set('address', e.target.value)} />
                        </Field>
                    </div>
                    <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
                        <span className="text-xs sm:me-auto">
                            {profileMsg && !profileMsg.field
                                ? <span className={profileMsg.ok ? 'text-green-600' : 'text-red-600'}>{profileMsg.text}</span>
                                : <span className="text-gray-500">{profileDirty ? `${Object.keys(profileChanges).length} alteração(ões) por guardar` : 'Sem alterações'}</span>}
                        </span>
                        <button type="submit" disabled={!profileDirty || savingProfile} className={btnPrimary}>
                            {savingProfile ? 'A guardar…' : 'Guardar dados'}
                        </button>
                    </div>
                </form>
            </Section>
        </div>
    );
}
